import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Message } from '@/lib/types';

export function appendMessage(messages: Message[], newMessage: Message): Message[] {
  return [...messages, newMessage];
}

export async function markConversationRead(
  conversationId: string,
  role: 'personal' | 'student'
): Promise<void> {
  const field = role === 'personal' ? 'unread_personal' : 'unread_student';
  await supabase
    .from('conversations')
    .update({ [field]: 0 })
    .eq('id', conversationId);
}

export function useMessages(conversationId: string): {
  messages: Message[];
  isLoading: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();

  const { data: messages = [], isLoading, error } = useQuery({
    queryKey: ['messages', conversationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as Message[];
    },
    staleTime: 0,
    enabled: !!conversationId,
  });

  useEffect(() => {
    if (!conversationId) return;
    const channel = supabase
      .channel(`messages:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          queryClient.setQueryData(
            ['messages', conversationId],
            (old: Message[] = []) => appendMessage(old, payload.new as Message)
          );
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [conversationId, queryClient]);

  return { messages, isLoading, error: error as Error | null };
}
